// Libs
import { zodResolver } from "@hookform/resolvers/zod";
import { useState, useMemo, useEffect } from "react";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { z } from "zod";

// Local
import { logger as devLogger } from "../../logging";
import * as strings from "../../strings";
import { ErrorMessage } from "../ErrorMessage";
import BaseButtonPrimary from "../BaseButtonPrimary";
import BaseButtonSecondary from "../BaseButtonSecondary";
import BaseInputText from "../BaseInputText";
import BaseInputTextArea from "../BaseInputTextArea";
import { handleTags } from "../../assets/js/HandleTags";
import { phoneUtils } from "../../utils/phoneUtils";
import { createToastMessageID } from "../../utils";
import { useToastMessageStore } from "../../store/toast-messages";
import { ExistingVendor } from "../../models/vendor";

interface Props {
  className: string;
  handleSave: (formValues: VendorFormValues) => Promise<void>;
  setVendorFormOpen: React.Dispatch<React.SetStateAction<boolean>>;
  vendor?: ExistingVendor; // Optional - if provided, we're editing; if not, we're creating
  children?: {
    VendorLogoSection?: React.ReactNode;
  };
}

// Schema for the form validation
const VendorFormManualSchema = z.object({
  name: z.string().min(1, { message: "Name is required" }),
  email: z
    .string()
    .email({ message: "Invalid email" })
    .optional()
    .or(z.literal("")),
  phone: z.string().optional().or(z.literal("")),
  notes: z.string().optional().or(z.literal("")),
  website: z
    .string()
    .url({ message: "Invalid website URL" })
    .optional()
    .or(z.literal("")),
  addressLine1: z.string().min(1, { message: "Address line 1 is required" }),
  addressLine2: z.string().optional().or(z.literal("")),
  city: z.string().min(1, { message: "City is required" }),
  state: z.string().min(1, { message: "State is required" }),
  zipCode: z.string().min(1, { message: "Zip code is required" }),
  country: z.string().min(1, { message: "Country is required" }),
  tags: z.string().optional().or(z.literal("")),
  paymentTerms: z.string().optional().or(z.literal("")),
});

// Type for the form values
type VendorFormManualState = z.infer<typeof VendorFormManualSchema>;

// Type for the values to return
export type VendorFormValues = {
  name: string;
  email: string[] | null;
  phone: string[] | null;
  notes: string | null;
  website: string | null;
  logoURL: string | null;
  addressLine1: string;
  addressLine2: string | null;
  city: string;
  state: string;
  zipCode: string;
  country: string;
  tags: string[];
  customData: Record<string, any>;
  deleted: boolean;
  paymentTerms: string | null;
};

export default function VendorFormManual({
  handleSave,
  setVendorFormOpen,
  vendor,
  children,
  ...props
}: Props) {
  const [displayError, setDisplayError] = useState<boolean>(false);
  const isEditing = !!vendor;

  const vendorFormDefaultValues: VendorFormManualState = useMemo(() => {
    if (isEditing && vendor) {
      return {
        name: vendor.name,
        email: vendor.email && vendor.email.length > 0 ? vendor.email[0] : "",
        phone: vendor.phone && vendor.phone.length > 0 ? vendor.phone[0] : "",
        notes: vendor.notes || "",
        website: vendor.website || "",
        addressLine1: vendor.addressLine1,
        addressLine2: vendor.addressLine2 || "",
        city: vendor.city,
        state: vendor.state,
        zipCode: vendor.zipCode,
        country: vendor.country,
        tags: vendor.tags.join(", "),
        paymentTerms: vendor.paymentTerms || "",
      };
    } else {
      return {
        name: "",
        email: "",
        phone: "",
        notes: "",
        website: "",
        addressLine1: "",
        addressLine2: "",
        city: "",
        state: "",
        zipCode: "",
        country: "USA",
        tags: "",
        paymentTerms: "Net 30",
      };
    }
  }, [vendor, isEditing]);

  const addToastMessage = useToastMessageStore(
    (state) => state.addToastMessage,
  );

  const {
    control,
    formState: { errors, isSubmitting },
    reset,
    handleSubmit,
  } = useForm<VendorFormManualState>({
    defaultValues: vendorFormDefaultValues,
    resolver: zodResolver(VendorFormManualSchema),
    mode: "onChange",
  });

  useEffect(() => {
    reset(vendorFormDefaultValues);
  }, [vendorFormDefaultValues, reset]);

  /* fn that handle all the states that needs to be reset when the dialog is closed */
  function closeAndReset() {
    reset();
    setDisplayError(false);
    setVendorFormOpen(false);
  }

  const onSubmit: SubmitHandler<VendorFormManualState> = async (formValues) => {
    let parsed;
    if (formValues.phone !== "") {
      const { PhoneNumber, warning } = phoneUtils.parse(formValues.phone || "");
      if (warning === true) {
        parsed = formValues.phone;
        addToastMessage({
          id: createToastMessageID(),
          message: strings.PHONE_TOO_SHORT,
          dialog: false,
          type: "warning",
        });
      } else {
        parsed = PhoneNumber.number;
      }
    }

    const vendorValues: VendorFormValues = {
      name: formValues.name,
      email: formValues.email ? [formValues.email] : null,
      phone: parsed ? [parsed.toString()] : null,
      notes: formValues.notes || null,
      website: formValues.website || null,
      logoURL: isEditing && vendor ? vendor.logoURL : null,
      addressLine1: formValues.addressLine1,
      addressLine2: formValues.addressLine2 || null,
      city: formValues.city,
      state: formValues.state,
      zipCode: formValues.zipCode,
      country: formValues.country,
      tags: handleTags(formValues.tags || ""),
      customData: isEditing && vendor ? vendor.customData : {},
      deleted: false,
      paymentTerms: formValues.paymentTerms || null,
    };

    try {
      await handleSave(vendorValues);
      closeAndReset();
    } catch (error) {
      setDisplayError(true);
      devLogger.error(error);
    }
  };

  const errorMessage = (
    <ErrorMessage
      message={
        isEditing ? strings.ERR_EDIT_VENDOR : strings.ERR_CREATE_NEW_VENDOR
      }
      clearMessage={() => setDisplayError(false)}
    />
  );

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className={props.className}
      noValidate
    >
      {displayError && errorMessage}

      {/* Vendor Information */}
      <div>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div>
            <Controller
              name="name"
              control={control}
              render={({ field }) => (
                <BaseInputText
                  admin={true}
                  text={strings.VENDOR_NAME}
                  onBlur={field.onBlur}
                  onChange={field.onChange}
                  ref={field.ref}
                  value={field.value}
                  id="name"
                  name={field.name}
                  required
                  type="text"
                />
              )}
            />
          </div>

          <div>
            <Controller
              name="phone"
              control={control}
              render={({ field }) => (
                <BaseInputText
                  admin={true}
                  text={strings.PHONE}
                  onBlur={field.onBlur}
                  onChange={field.onChange}
                  ref={field.ref}
                  value={field.value}
                  id="phone"
                  name={field.name}
                  type="tel"
                />
              )}
            />
          </div>

          <div>
            <Controller
              name="email"
              control={control}
              render={({ field }) => (
                <BaseInputText
                  admin={true}
                  text={strings.EMAIL}
                  onBlur={field.onBlur}
                  onChange={field.onChange}
                  ref={field.ref}
                  value={field.value}
                  id="email"
                  name={field.name}
                  type="email"
                />
              )}
            />
          </div>

          <div>
            <Controller
              name="website"
              control={control}
              render={({ field }) => (
                <BaseInputText
                  admin={true}
                  text={strings.WEBSITE}
                  onBlur={field.onBlur}
                  onChange={field.onChange}
                  ref={field.ref}
                  value={field.value}
                  id="website"
                  name={field.name}
                  type="url"
                />
              )}
            />
          </div>

          <div>
            <Controller
              name="paymentTerms"
              control={control}
              render={({ field }) => (
                <BaseInputText
                  admin={true}
                  text={strings.PAYMENT_TERMS}
                  onBlur={field.onBlur}
                  onChange={field.onChange}
                  ref={field.ref}
                  value={field.value}
                  id="paymentTerms"
                  name={field.name}
                  type="text"
                />
              )}
            />
          </div>

          <div>
            <Controller
              name="tags"
              control={control}
              render={({ field }) => (
                <BaseInputText
                  admin={true}
                  text={strings.TAGS}
                  onBlur={field.onBlur}
                  onChange={field.onChange}
                  ref={field.ref}
                  value={field.value}
                  id="tags"
                  name={field.name}
                  type="text"
                  placeholder="tag1, tag2, tag3"
                />
              )}
            />
          </div>
        </div>
      </div>

      {/* Address Information */}
      <div>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div>
            <Controller
              name="addressLine1"
              control={control}
              render={({ field }) => (
                <BaseInputText
                  admin={true}
                  text={strings.ADDRESS_LINE_1}
                  onBlur={field.onBlur}
                  onChange={field.onChange}
                  ref={field.ref}
                  value={field.value}
                  id="addressLine1"
                  name={field.name}
                  required
                  type="text"
                />
              )}
            />
          </div>

          <div>
            <Controller
              name="addressLine2"
              control={control}
              render={({ field }) => (
                <BaseInputText
                  admin={true}
                  text={strings.ADDRESS_LINE_2}
                  onBlur={field.onBlur}
                  onChange={field.onChange}
                  ref={field.ref}
                  value={field.value}
                  id="addressLine2"
                  name={field.name}
                  type="text"
                />
              )}
            />
          </div>

          <div>
            <Controller
              name="city"
              control={control}
              render={({ field }) => (
                <BaseInputText
                  admin={true}
                  text={strings.CITY}
                  onBlur={field.onBlur}
                  onChange={field.onChange}
                  ref={field.ref}
                  value={field.value}
                  id="city"
                  name={field.name}
                  required
                  type="text"
                />
              )}
            />
          </div>

          <div>
            <Controller
              name="state"
              control={control}
              render={({ field }) => (
                <BaseInputText
                  admin={true}
                  text={strings.STATE}
                  onBlur={field.onBlur}
                  onChange={field.onChange}
                  ref={field.ref}
                  value={field.value}
                  id="state"
                  name={field.name}
                  required
                  type="text"
                />
              )}
            />
          </div>

          <div>
            <Controller
              name="zipCode"
              control={control}
              render={({ field }) => (
                <BaseInputText
                  admin={true}
                  text={strings.ZIP}
                  onBlur={field.onBlur}
                  onChange={field.onChange}
                  ref={field.ref}
                  value={field.value}
                  id="zipCode"
                  name={field.name}
                  required
                  type="text"
                />
              )}
            />
          </div>

          <div>
            <Controller
              name="country"
              control={control}
              render={({ field }) => (
                <BaseInputText
                  admin={true}
                  text={strings.COUNTRY}
                  onBlur={field.onBlur}
                  onChange={field.onChange}
                  ref={field.ref}
                  value={field.value}
                  id="country"
                  name={field.name}
                  required
                  type="text"
                />
              )}
            />
          </div>
        </div>
      </div>

      {/* Notes */}
      <div>
        <Controller
          name="notes"
          control={control}
          render={({ field }) => (
            <BaseInputTextArea
              admin={true}
              rows={4}
              text={strings.NOTES}
              onBlur={field.onBlur}
              onChange={field.onChange}
              ref={field.ref}
              value={field.value}
              id="notes"
              name={field.name}
            />
          )}
        />
      </div>

      {/* Vendor Logo Section */}
      {children?.VendorLogoSection && children.VendorLogoSection}

      {/* Action Buttons */}
      <div className="flex justify-end space-x-3 pt-4">
        <BaseButtonSecondary
          onClick={closeAndReset}
          type="button"
          disabled={isSubmitting}
        >
          {strings.buttons.CANCEL}
        </BaseButtonSecondary>
        <BaseButtonPrimary
          type="submit"
          disabled={isSubmitting}
          isBusy={isSubmitting}
          busyText={strings.buttons.BUSY_SAVING}
        >
          {strings.buttons.SAVE}
        </BaseButtonPrimary>
      </div>
    </form>
  );
}
